import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, IconButton, Stack, Typography, alpha } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import BalanceIcon from '@mui/icons-material/Balance';

const SLOTS = [
  { key: 'mil1', label: 'Milliman PLIG', hint: "'Prosperity'/'PLIG' in filename", accent: '#5b7fff' },
  { key: 'mil2', label: 'Milliman NWL', hint: "'NWL' in filename", accent: '#5b7fff' },
  { key: 'cwPos', label: 'CW Positions', hint: "'Position' in filename", accent: '#38bdf8' },
  { key: 'cwTx', label: 'CW Transactions', hint: "'Derivative Transaction' in filename", accent: '#38bdf8' },
  { key: 'ntFile', label: 'NT Custody (NWL)', hint: "'Cash_Activity_Detail' in filename", accent: '#34d399' },
  { key: 'jpmFile', label: 'JPM Custody (SUSA/SBLI)', hint: "'All Transaction Activity' in filename", accent: '#34d399' },
];

function classify(f) {
  const n = f.name.toLowerCase();
  if (n.includes('nwl')) return 'mil2';
  if (n.includes('prosperity') || n.includes('plig')) return 'mil1';
  if (n.includes('cash_activity') || n.includes('cash activity') || n.includes('northern')) return 'ntFile';
  if (n.includes('all transaction') || n.includes('jpm')) return 'jpmFile';
  if (n.includes('derivative transaction') || n.includes('option transaction') || n.includes(' tx')) return 'cwTx';
  if (n.includes('position') || n.includes('pos')) return 'cwPos';
  if (n.includes('transaction')) return 'cwTx';
  return null;
}

export default function UploadView({ files, setFiles, onRun, loading, error, setError }) {
  const [dragOver, setDragOver] = useState(false);
  const allReady = files.mil1 && files.mil2 && files.cwPos && files.cwTx;

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = Array.from(e.dataTransfer.files).filter((f) => f.name.match(/\.(xlsx?|csv)$/i));
      const updates = {};
      const unmatched = [];
      dropped.forEach((f) => {
        const k = classify(f);
        if (k) updates[k] = f;
        else unmatched.push(f.name);
      });
      if (unmatched.length) setError('Could not auto-classify: ' + unmatched.join(', '));
      else setError(null);
      setFiles((p) => ({ ...p, ...updates }));
    },
    [setFiles, setError],
  );

  return (
    <Box sx={{ minHeight: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Card sx={{ maxWidth: 760, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack alignItems="center" spacing={1} mb={3}>
            <BalanceIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={800}>Derivative Reconciliation</Typography>
            <Typography variant="body2" color="text.secondary">
              Drop all files at once — they'll be classified automatically
            </Typography>
          </Stack>

          <Box
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            sx={{ border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'divider', borderRadius: 2, p: 4, textAlign: 'center', bgcolor: dragOver ? alpha('#5b7fff', 0.06) : 'transparent', transition: 'all .15s', mb: 3 }}
          >
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography fontWeight={700} fontSize={14}>Drop all files here</Typography>
            <Typography variant="caption" color="text.secondary">or use the slots below to assign individually</Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 3 }}>
            {SLOTS.map((item) => {
              const f = files[item.key];
              const filled = !!f;
              return (
                <Box key={item.key} component="label"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, border: '2px solid', borderColor: filled ? item.accent : 'divider', bgcolor: filled ? alpha(item.accent, 0.08) : 'transparent', cursor: 'pointer', transition: 'all .15s', minWidth: 0, '&:hover': { borderColor: alpha(item.accent, 0.6) } }}
                >
                  {filled
                    ? <CheckCircleIcon sx={{ color: item.accent, fontSize: 22 }} />
                    : <Box sx={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
                  }
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={12} fontWeight={700} color={filled ? 'text.primary' : 'text.secondary'}>{item.label}</Typography>
                    {filled
                      ? <Typography fontSize={10} sx={{ color: item.accent, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Typography>
                      : <Typography fontSize={10} color="text.secondary">{item.hint}</Typography>
                    }
                  </Box>
                  {filled && (
                    <IconButton size="small" onClick={(e) => { e.preventDefault(); setFiles((p) => ({ ...p, [item.key]: null })); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files[0]; if (file) setFiles((p) => ({ ...p, [item.key]: file })); }} />
                </Box>
              );
            })}
          </Box>

          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

          <Button variant="contained" fullWidth disabled={!allReady || loading} onClick={onRun}
            sx={{ py: 1.5, fontSize: 14, fontWeight: 700, background: 'linear-gradient(135deg, #5b7fff, #3d5bd6)' }}
          >
            {loading ? 'Running reconciliation…' : 'Run Reconciliation →'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
